import React from "react";
import {
  Typography,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from "@mui/material";
import produce from "immer";
import { initialState } from "./initialState";

export const autoRefractionHeaders = ["Auto Refraction", "SPH", "CYL", "Axis"];

interface AutoRefractionProps {
  visionData: typeof initialState;
  onVisionChange: (updatedVision: typeof initialState) => void;
}

const ROWS = ["OD", "OS"] as const;
const COLS = [
  { key: "sph", label: "SPH" },
  { key: "cyl", label: "CYL" },
  { key: "axis", label: "Axis" }
] as const;

export default function AutoRefraction({
  visionData,
  onVisionChange
}: AutoRefractionProps): React.JSX.Element {
  const handleChange = (
    row: (typeof ROWS)[number],
    col: (typeof COLS)[number]["key"],
    value: string
  ) => {
    const updated = produce(visionData, (draft: typeof initialState) => {
      if (!draft.vision) {
        draft.vision = JSON.parse(JSON.stringify(initialState.vision));
      }
      const { autoRefraction } = draft.vision;
      const eyeData = autoRefraction?.[row] as unknown as Record<string, string | null>;
      if (eyeData) {
        eyeData[col] = value === "" ? null : value;
      }
    });
    onVisionChange(updated);
  };

  const currentAutoRefraction = visionData?.vision?.autoRefraction || {};

  const cellStyle = {
    border: 1,
    borderColor: "divider"
  };

  const headerCellStyle = {
    ...cellStyle,
    fontWeight: 600,
    textAlign: "center" as const
  };

  return (
    <TableContainer>
      <Typography
        sx={{
          padding: 1,
          textAlign: "center",
          backgroundColor: "#ededed",
          borderLeft: 1,
          borderRight: 1,
          borderTop: 1,
          borderColor: "divider"
        }}
      >
        Auto Refraction
      </Typography>

      <Table size="small" sx={{ border: 1, borderColor: "divider", borderRadius: "5px" }}>
        <TableHead>
          <TableRow>
            <TableCell sx={{ ...headerCellStyle, width: "100px" }} />
            {COLS.map((col) => (
              <TableCell key={col.key} sx={headerCellStyle}>
                {col.label}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {ROWS.map((row) => (
            <TableRow key={row}>
              <TableCell sx={{ ...headerCellStyle, textAlign: "left" }}>{row}</TableCell>
              {COLS.map((col) => (
                <TableCell key={col.key} sx={cellStyle}>
                  <TextField
                    size="small"
                    variant="outlined"
                    fullWidth
                    value={currentAutoRefraction[row]?.[col.key] ?? ""}
                    onChange={(e) => handleChange(row, col.key, e.target.value)}
                    sx={{
                      "& .MuiInputBase-input": {
                        padding: "4px 8px",
                        fontSize: "0.875rem"
                      }
                    }}
                  />
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
